import { Fn }			from "ts-base/fn";
import { Endo }			from "ts-base/endo";
import { Is }			from "ts-base/is";
import { Predicate }	from "ts-base/predicate";
import { Maybe }		from "ts-base/maybe";
import { Validated }	from "ts-base/validated";
import { Either }		from "ts-base/either";
import { Maps }			from "ts-base/maps";
import { Sets }			from "ts-base/sets";

export type Zoom<T, S> = Fn<Endo<T>, Endo<S>>;

export namespace Zoom {
	export const identity = <T>():Zoom<T, T> =>
		Fn.identity;

	/** nasty hack, avoid this */
	export const cast = <S, T>():Zoom<S, T> =>
		mod => orig => mod(orig as unknown as S) as unknown as T;

	/** only modify a value when it matches some condition */
	export const when = <T>(cond:Predicate<T>):Zoom<T, T> =>
		mod => orig => cond(orig) ? mod(orig) : orig;

	/** only modify a value when it is a guarded subtype */
	export const is	= <T extends S, S>(is:Is<S, T>):Zoom<T, S> =>
		mod => orig => is(orig) ? mod(orig) : orig;

	/** modify an object property at a given key */
	export const atKey = <S, K extends keyof S>(key:K):Zoom<S[K], S> =>
		mod => orig => ({ ...orig, [key]:mod(orig[key]) });

	/** modify an array element at the given index */
	export const atIndexMut = <T>(index:number):Zoom<T, Array<T>> =>
		mod => orig => index >= 0 && index < orig.length ? [ ...orig.slice(0, index), mod(orig[index]), ...orig.slice(index + 1) ] : orig;

	/** modify an array element at the given index */
	export const atIndexRo = <T>(index:number):Zoom<T, ReadonlyArray<T>> =>
		mod => orig => index >= 0 && index < orig.length ? [ ...orig.slice(0, index), mod(orig[index]), ...orig.slice(index + 1) ] : orig;

	/** modify each element of an array */
	export const eachMut = <T>():Zoom<T, Array<T>> =>
		mod => orig => orig.map(mod);

	/** modify each element of an array */
	export const eachRo = <T>():Zoom<T, ReadonlyArray<T>> =>
		mod => orig => orig.map(mod);

	/** modify a value if it's not null */
	export const notNull = <T>():Zoom<T, T|null> =>
		mod => orig => orig !== null ? mod(orig) : null;

	/** modify the Some of a Maybe */
	export const some	= <T>():Zoom<T, Maybe<T>>	=>
		mod => orig => Maybe.map(orig)(mod);

	/** modify the Valid of a Validated */
	export const valid	= <E, T>():Zoom<T, Validated<E, T>>	=>
		mod => orig => Validated.map(orig)(mod);

	/*
	// TODO zoom clarify: we could modify each error separately
	// or we could modify all errors, but then we should have a NonEmptyArray
	export const invalid	= <E,T>():Zoom<E,Validated<E,T>>	=>
		mod => orig => validated.mapErrors(orig)(mod);
	*/

	/** modify the Right of an Either */
	export const right	= <E, T>():Zoom<T, Either<E, T>>	=>
		mod => orig => Either.map(orig)(mod);

	/** modify the Right of an Either */
	export const left	= <E, T>():Zoom<E, Either<E, T>>	=>
		mod => orig => Either.mapError(orig)(mod);

	/** modify array elements matching a condition */
	export const whereMut = <T>(cond:Predicate<T>):Zoom<T, Array<T>> =>
		mod => orig => orig.map((it:T) => cond(it) ? mod(it) : it);

	/** modify array elements matching a condition */
	export const whereRo = <T>(cond:Predicate<T>):Zoom<T, ReadonlyArray<T>> =>
		mod => orig => orig.map((it:T) => cond(it) ? mod(it) : it);

	/** modify array elements at matching indices */
	export const whereIndexMut = <T>(cond:Predicate<number>):Zoom<T, Array<T>> =>
		mod => orig => orig.map((it:T, idx:number) => cond(idx) ? mod(it) : it);

	/** modify array elements at matching indices */
	export const whereIndexRo = <T>(cond:Predicate<number>):Zoom<T, ReadonlyArray<T>> =>
		mod => orig => orig.map((it:T, idx:number) => cond(idx) ? mod(it) : it);

	/** modify array elements chosen by value, index and length */
	export const whereValueIndexLengthMut = <T>(cond:(item:T, index:number, length:number)=>boolean):Zoom<T, Array<T>> =>
		mod => orig => orig.map((it:T, idx:number) => cond(it, idx, orig.length) ? mod(it) : it);

	/** modify array elements chosen by value, index and length */
	export const whereValueIndexLengthRo = <T>(cond:(item:T, index:number, length:number)=>boolean):Zoom<T, ReadonlyArray<T>> =>
		mod => orig => orig.map((it:T, idx:number) => cond(it, idx, orig.length) ? mod(it) : it);

	/** modify the value of a Map at a given key */
	export const mapAtKeyMut = <K, T>(key:K):Zoom<T, Map<K, T>> =>
		mod => orig => Maps.modify(orig)(key)(mod);

	/** modify the value of a Map at a given key */
	export const mapAtKeyRo = <K, T>(key:K):Zoom<T, ReadonlyMap<K, T>> =>
		mod => orig => Maps.modify(orig)(key)(mod);

	/** modify the possibly existing value of a Map at a given key */
	export const mapMaybeAtKeyMut	= <K, T>(key:K):Zoom<Maybe<T>, Map<K, T>>	=>
		mod => orig => Maps.modifyMember(orig)(key)(mod);

	/** modify the possibly existing value of a Map at a given key */
	export const mapMaybeAtKeyRo	= <K, T>(key:K):Zoom<Maybe<T>, ReadonlyMap<K, T>>	=>
		mod => orig => Maps.modifyMember(orig)(key)(mod);

	/** modify the membership of a given value in a Set */
	export const setAtValueMut	= <T>(value:T):Zoom<boolean, Set<T>>	=>
		mod => orig => Sets.modifyMember(orig)(value)(mod);

	/** modify the membership of a given value in a Set */
	export const setAtValueRo	= <T>(value:T):Zoom<boolean, ReadonlySet<T>>	=>
		mod => orig => Sets.modifyMember(orig)(value)(mod);

	/** modify only some fields of an object */
	export const partialObject	= <S extends T, T extends object>():Zoom<T, S>	=>
		(tt:Endo<T>):Endo<S> => (s:S):S => ({
			...s,
			...tt(s),
		});
}
